import { useEffect, useState } from 'react';
import { apiGetFunnel, apiUpdateFunnel, type FunnelDetail, type FunnelStatus } from '~/lib/api/funnels';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  Field,
  LoadError,
  Pill,
  Section,
  Segmented,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { AdminExtensionSlot } from '~/components/workbench/admin-extensions';
import { FunnelStatsView } from './FunnelStatsView';
import { apiErrorMessage, draftsFromSteps, funnelStatusMeta, stepProblems } from './funnels-model';
import { OffersEditor } from './OffersEditor';
import { StepsEditor } from './StepsEditor';

type Tab = 'steps' | 'offers' | 'stats';

export function FunnelEditor({ appId, funnelId, onBack }: { appId: string; funnelId: string; onBack: () => void }) {
  const detail = useResource(() => apiGetFunnel(appId, funnelId), `${appId}:${funnelId}`);
  const [tab, setTab] = useState<Tab>('steps');
  const data = detail.data;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={onBack} className={BTN_GHOST}>
          <div className="i-ph:arrow-left text-[13px]" />
          All funnels
        </button>
        {data && (
          <Pill tone={funnelStatusMeta(data.funnel.status).tone} label={funnelStatusMeta(data.funnel.status).label} />
        )}
      </div>

      {data ? (
        <>
          <FunnelSettings appId={appId} detail={data} onSaved={(funnel) => detail.setData({ ...data, funnel })} />

          <div className="flex items-start gap-2 rounded-lg border border-hairline bg-sunken px-3 py-2.5">
            <div className="i-ph:info mt-px shrink-0 text-[13px] text-info" />
            <p className="text-[11px] leading-relaxed text-muted">
              Upsells and downsells currently open a second, pre-filled checkout. One-click upsells come later.
            </p>
          </div>

          <Segmented
            label="Funnel section"
            value={tab}
            options={[
              ['steps', `Steps (${data.steps.length})`],
              ['offers', `Offers (${data.offers.length})`],
              ['stats', 'Stats'],
            ]}
            onChange={setTab}
          />

          {/* Kept mounted so unsaved step edits survive a visit to Offers (e.g. to add one). */}
          <div className={tab === 'steps' ? undefined : 'hidden'}>
            <StepsEditor appId={appId} detail={data} onSaved={detail.setData} />
          </div>
          {tab === 'offers' && <OffersEditor appId={appId} detail={data} onChanged={() => void detail.reload()} />}
          {tab === 'stats' && <FunnelStatsView appId={appId} detail={data} />}
          <AdminExtensionSlot appId={appId} target="funnels.funnel.details" resourceId={funnelId} />
        </>
      ) : detail.error ? (
        <LoadError message="Could not load this funnel." onRetry={() => void detail.reload()} />
      ) : (
        <>
          <SkeletonCard lines={3} />
          <SkeletonCard lines={4} />
        </>
      )}
    </div>
  );
}

function FunnelSettings({
  appId,
  detail,
  onSaved,
}: {
  appId: string;
  detail: FunnelDetail;
  onSaved: (funnel: FunnelDetail['funnel']) => void;
}) {
  const f = detail.funnel;
  const [name, setName] = useState(f.name);
  const [slug, setSlug] = useState(f.slug);
  const [busy, setBusy] = useState<'details' | FunnelStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const savedProblems = stepProblems(draftsFromSteps(detail.steps), new Set(detail.offers.map((o) => o.id)));
  const canActivate = savedProblems.length === 0;
  const dirty = name.trim() !== f.name || slug.trim() !== f.slug;

  useEffect(() => {
    setName(f.name);
    setSlug(f.slug);
  }, [f.name, f.slug]);

  const patch = async (kind: 'details' | FunnelStatus, body: Parameters<typeof apiUpdateFunnel>[2]) => {
    setBusy(kind);
    setError(null);

    try {
      onSaved(await apiUpdateFunnel(appId, f.id, body));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update this funnel.'));
    } finally {
      setBusy(null);
    }
  };

  const saveDetails = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('A funnel needs a name.');
      return;
    }

    void patch('details', { name: name.trim(), ...(slug.trim() !== f.slug ? { slug: slug.trim() } : {}) });
  };

  return (
    <Section title="Funnel" icon="i-ph:funnel">
      <form onSubmit={saveDetails} className={classNames(CARD, 'space-y-3 p-4')}>
        <div className="flex gap-2">
          <div className="min-w-0 flex-1">
            <Field label="Name">
              <input value={name} onChange={(e) => setName(e.target.value)} className="ui-input !py-2" />
            </Field>
          </div>
          <div className="min-w-0 flex-1">
            <Field label="Slug">
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                spellCheck={false}
                className="ui-input-mono !py-2"
              />
            </Field>
          </div>
        </div>

        {dirty && (
          <button type="submit" disabled={busy !== null} className={BTN_SECONDARY}>
            {busy === 'details' && <Spinner />}
            Save name
          </button>
        )}

        <div className="space-y-1.5">
          <span className="block text-[11px] text-secondary">Status · {f.currency}</span>
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              label="Funnel status"
              value={f.status}
              options={[
                ['draft', 'Draft'],
                ['active', 'Active'],
                ['archived', 'Archived'],
              ]}
              onChange={(next) => {
                if (next === f.status || busy !== null) {
                  return;
                }

                if (next === 'active' && !canActivate) {
                  setError(`Fix the steps before activating: ${savedProblems[0]}`);
                  return;
                }

                void patch(next, { status: next });
              }}
            />
            {busy && busy !== 'details' && <Spinner />}
          </div>
          <p className="text-[11px] leading-relaxed text-muted">
            {f.status === 'active'
              ? 'Live on your published site.'
              : f.status === 'archived'
                ? 'Hidden everywhere. Past purchases are kept.'
                : 'Drafts are only visible in the preview and test checkouts.'}
          </p>
        </div>

        {error && <ErrorBanner message={error} />}
      </form>
    </Section>
  );
}
